import { useMemo, useState } from "react";
import type { MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import {
  countShelfAttention,
  shelfLineNeedsAttention,
  shelfVarianceIsGap,
  shelfVarianceReportLine,
  sortShelfVarianceLines,
  summarizeShelfVariance,
  type ShelfVarianceSort,
} from "@/lib/manufacturing/cogsShelfVarianceReport";
import { DEFAULT_SHELF_ALERT_PCT, shelfAlertPercentLabel } from "@/lib/manufacturing/cogsShelfVariance";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { SHELF_ATTENTION_TONE, SHELF_VARIANCE_TONE } from "./PricingBandTotalCell";
import { formatUsd } from "./pricingGridFormat";

const SORTS: { id: ShelfVarianceSort; label: string }[] = [
  { id: "largest", label: "Largest gap" },
  { id: "wholesale", label: "Wholesale gap" },
  { id: "msrp", label: "MSRP gap" },
  { id: "name", label: "Name" },
];

function summaryText(label: string, summary: ReturnType<typeof summarizeShelfVariance>): string {
  const net = summary.net_delta_usd;
  const signed = `${net > 0 ? "+" : net < 0 ? "−" : ""}${formatUsd(Math.abs(net))}`;
  return `${label}: ${summary.above} above, ${summary.below} below, ${summary.match} match, ${summary.missing} missing. Net ${signed}.`;
}

function attentionClass(attention: "alert" | "warning" | "quiet", tone: keyof typeof SHELF_VARIANCE_TONE): string {
  return attention === "quiet" ? SHELF_VARIANCE_TONE[tone] : SHELF_ATTENTION_TONE[attention];
}

export function PricingShelfVarianceReport({
  rows,
  selectedKey,
  onSelect,
  alertThresholdPct = DEFAULT_SHELF_ALERT_PCT,
}: {
  rows: readonly MfgPricingLayerGridRow[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  alertThresholdPct?: number;
}) {
  const [sort, setSort] = useState<ShelfVarianceSort>("largest");
  const [gapsOnly, setGapsOnly] = useState(true);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const all = useMemo(
    () => rows.map((row) => shelfVarianceReportLine(row, alertThresholdPct)),
    [rows, alertThresholdPct],
  );
  const lines = useMemo(() => {
    const visible = all.filter((line) => {
      if (attentionOnly) return shelfLineNeedsAttention(line);
      if (gapsOnly) return shelfVarianceIsGap(line);
      return true;
    });
    return sortShelfVarianceLines(visible, sort);
  }, [all, sort, gapsOnly, attentionOnly]);
  const wholesale = summarizeShelfVariance(all, "wholesale");
  const msrp = summarizeShelfVariance(all, "msrp");
  const attention = countShelfAttention(all);
  const thresholdLabel = shelfAlertPercentLabel(alertThresholdPct);

  return (
    <div className="mb-3 space-y-2">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        {summaryText("Wholesale", wholesale)} {summaryText("MSRP", msrp)} {attention.alerts} cross the {thresholdLabel} alert. {attention.warnings} are warnings because materials are incomplete. Counts use the current filters.
      </p>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-600 dark:text-zinc-400">Sort</span>
          <select className={inputClass} value={sort} onChange={(event) => setSort(event.target.value as ShelfVarianceSort)}>
            {SORTS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
          <input type="checkbox" checked={gapsOnly} onChange={(event) => setGapsOnly(event.target.checked)} disabled={attentionOnly} />
          Only gaps
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
          <input type="checkbox" checked={attentionOnly} onChange={(event) => setAttentionOnly(event.target.checked)} />
          Alerts and warnings
        </label>
      </div>
      {lines.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          {attentionOnly ? "No shelf alerts or warnings in the current filters." : "No shelf gaps in the current filters."}
        </p>
      ) : (
        <div className="max-h-80 overflow-auto rounded-xl border border-gray-200 dark:border-zinc-800">
          <table className="min-w-full text-left text-xs">
            <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
              <tr>
                <th className="px-2 py-2 font-semibold">Item</th>
                <th className="px-2 py-2 font-semibold">Size</th>
                <th className="px-2 py-2 text-right font-semibold">Wholesale</th>
                <th className="px-2 py-2 text-right font-semibold">MSRP</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr
                  key={line.key}
                  className={`cursor-pointer border-t border-gray-100 dark:border-zinc-800 ${
                    line.key === selectedKey ? "bg-indigo-50 dark:bg-indigo-950/30" : "hover:bg-gray-50 dark:hover:bg-zinc-900"
                  }`}
                  onClick={() => onSelect(line.key)}
                >
                  <td className="px-2 py-1.5">
                    <div className="font-medium">{line.name}</div>
                    <div className="text-[10px] text-gray-500 dark:text-zinc-400">{line.code}</div>
                  </td>
                  <td className="px-2 py-1.5 text-gray-600 dark:text-zinc-400">{line.size_band}</td>
                  <td className={`px-2 py-1.5 text-right ${attentionClass(line.wholesale_attention, line.wholesale.variance.tone)}`}>
                    {line.wholesale.variance.label}
                  </td>
                  <td className={`px-2 py-1.5 text-right ${attentionClass(line.msrp_attention, line.msrp.variance.tone)}`}>
                    {line.msrp.variance.label}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
